import { DEFAULT_LANGUAGE } from "../../config/i18n";

export type CongressTranslation = {
  language: string;
  // Every field is `string | null | undefined` because this resolver consumes
  // the persisted-cache boundary: the webapp persists react-query bodies to
  // localStorage with ~infinite gcTime, so a returning user can rehydrate a
  // PRE-deploy congress body under NEW code (e.g. one minted before city/country
  // existed). Such a field reads back `undefined` at runtime even though the
  // live wire types it `string | null`. Trust nothing here — undefined, null and
  // "" are all normalized to "absent" (see `present` below). Refs
  // .knowledge/react-query-persisted-stale-shape.md.
  name: string | null | undefined;
  shortName: string | null | undefined;
  description: string | null | undefined;
  city: string | null | undefined;
  country: string | null | undefined;
};

/**
 * Resolve a congress's long name, short name, description + location for the
 * content language, falling back to the default language then the legacy
 * congress name (description falls back to ""; location falls back to the
 * single `legacyCity`/`legacyCountry`, then null). Shared by the Discovery
 * hero, the Resources section and the Top Sessions page.
 *
 * `lang` is the CONTENT language (the room's language selector), NOT the
 * interface locale — a congress name/place is content, so it follows the same
 * axis the payload is queried on, like articles do.
 */
export function resolveCongressNames(
  translations: CongressTranslation[],
  fallbackName: string,
  lang: string,
  legacyCity: string | null,
  legacyCountry: string | null
): {
  name: string;
  shortName: string;
  description: string;
  city: string | null;
  country: string | null;
} {
  const active = translations.find((tr) => tr.language === lang);
  const fallback = translations.find((tr) => tr.language === DEFAULT_LANGUAGE);

  // The single normalizer for the whole resolver: undefined, null and "" all
  // mean "absent" → null. Every field flows through this exactly once, so there
  // is one level of defensiveness, not two.
  const present = (value: string | null | undefined): string | null =>
    value !== undefined && value !== null && value !== "" ? value : null;

  // First present value across the content-language entry then the
  // default-language entry; null if neither has one.
  const fromTranslations = (
    get: (tr: CongressTranslation) => string | null | undefined
  ): string | null => {
    const fromActive = active === undefined ? null : present(get(active));
    if (fromActive !== null) return fromActive;
    return fallback === undefined ? null : present(get(fallback));
  };

  // Text: entry → entry → the string fallback (never null).
  const pickText = (
    get: (tr: CongressTranslation) => string | null | undefined,
    fallbackValue: string
  ): string => {
    const resolved = fromTranslations(get);
    return resolved !== null ? resolved : fallbackValue;
  };

  // Location: entry → entry → the legacy single value → null. Returning null
  // (never "") lets the label builders' `.filter(non-empty)` drop it cleanly, so
  // a missing country never leaves a dangling "City, " comma or a stray
  // "date | " separator.
  const pickLocation = (
    get: (tr: CongressTranslation) => string | null | undefined,
    legacyValue: string | null
  ): string | null => {
    const resolved = fromTranslations(get);
    return resolved !== null ? resolved : present(legacyValue);
  };

  return {
    name: pickText((tr) => tr.name, fallbackName),
    shortName: pickText((tr) => tr.shortName, fallbackName),
    description: pickText((tr) => tr.description, ""),
    city: pickLocation((tr) => tr.city, legacyCity),
    country: pickLocation((tr) => tr.country, legacyCountry),
  };
}
